// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Pagination from "@insite/mobius/Pagination";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pacific/App/Components/Notify";
import loadBlogs from "@pacific/App/Handlers/Blog/LoadBlogs";
import { BlogModel } from "@pacific/App/Services/BlogService";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import TextField, { TextFieldPresentationProps } from "@pacific/MobiusOverrides/TextField";
import Search from "@pacific/XDL/Icons/Search";
import { color, ellipsis, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import debounce from "lodash/debounce";
import * as React from "react";
import { ResolveThunks, connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        blogs: state.blogs.blogs,
        isLoading: state.blogs.isLoading,
        pagination: state.blogs.pagination,
        isAuthenticated: xIsAuthenticated(state),
        session: getCustomTypeSession(state),
        currentBillTo: getCurrentBillToState(state).value,
    };
};

const mapDispatchToProps = {
    loadBlogs,
};

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BlogListStyles {
    topSection?: InjectableCss;
    searchInput?: TextFieldPresentationProps;
    blogCard?: InjectableCss;
    image?: LazyImageProps;
    title?: TypographyProps;
    date?: TypographyProps;
    summary?: TypographyProps;
}

export const blogListStyles: BlogListStyles = {
    topSection: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 32px;
            border-bottom: 1px solid ${color("common.border")};
            ${xScreen("lte", "sm")} {
                flex-direction: column;
            }
        `,
    },
    searchInput: {
        cssOverrides: {
            formField: css`
                max-width: 300px;
                margin-bottom: 5px;
            `,
        },
        sizeVariant: "default",
        iconProps: { src: Search, color: "common.ui4" },
    },
    blogCard: {
        css: css`
            border: 1px solid ${color("common.border")};
            border-top: 4px solid ${color("primary")};
            padding: 12px;
            display: block;
            width: 100%;
            height: 100%;
        `,
    },
    image: {
        css: css`
            width: 100%;
            max-height: 100%;
            margin-bottom: 12px;
            height: 97px;
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin-bottom: 4px;
        `,
    },
    date: {
        variant: "h6",
        weight: "normal",
    },
    summary: {
        css: css`
            ${ellipsis(3)}
        `,
    },
};

const styles = blogListStyles;

export const BlogList: React.FC<Props> = ({
    blogs,
    isLoading,
    pagination,
    loadBlogs,
    isAuthenticated,
    currentBillTo,
    session,
}) => {
    const [query, setQuery] = React.useState("");
    let sortedBlogs: any = [];
    Object.assign(sortedBlogs, blogs);
    sortedBlogs =
        sortedBlogs?.sort((blog1: BlogModel, blog2: BlogModel) => {
            return new Date(blog2.date).getTime() - new Date(blog1.date).getTime();
        }) || [];

    React.useEffect(() => {
        loadBlogs({});
    }, []);

    const changePage = (newPageIndex: number) => {
        loadBlogs({ page: newPageIndex, query });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadBlogs({
            page: 1,
            pageSize: newPageSize,
            query,
        });
    };

    const handleInputChange = debounce(event => {
        setQuery(event.target.value);
        loadBlogs({ query: event.target.value });
    }, 500);

    const _GaBlogClickEvent = (title?: string, url?: string) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }
        const googleAnalyticsData = {
            event: "blog",
            click_text: title,
            destination: url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.topSection}>
                <TextField
                    name="query"
                    onChange={handleInputChange}
                    placeholder={translate("Search Blogs")}
                    {...styles.searchInput}
                />
                {pagination && !!blogs?.length && (
                    <Pagination
                        resultsCount={pagination.totalItemCount}
                        currentPage={pagination.page}
                        resultsPerPage={pagination.pageSize}
                        resultsPerPageOptions={pagination.pageSizeOptions}
                        onChangePage={changePage}
                        onChangeResultsPerPage={changeResultsPerPage}
                    />
                )}
            </StyledWrapper>
            <GridContainer gap={22}>
                {query && !blogs?.length && (
                    <GridItem width={12}>
                        <Notify type="warning" showClose={false}>
                            {siteMessage("Custom_Blogs_NoBlogsFound")}
                        </Notify>
                    </GridItem>
                )}
                {sortedBlogs?.map((blog: any) => (
                    <GridItem width={[12, 6, 4, 3, 3]} key={blog.title}>
                        <Clickable
                            href={blog.url}
                            {...styles.blogCard}
                            aria-label="blog card clicable"
                            onClick={() => _GaBlogClickEvent(blog.title, blog.url)}
                        >
                            <LazyImage src={blog.cardImage} {...styles.image} altText={blog.title} />
                            <Typography {...styles.title}>{blog.title}</Typography>
                            <Typography {...styles.date}>{blog.date}</Typography>
                            <Typography {...styles.summary}>{blog.cardSummary}</Typography>
                        </Clickable>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BlogList),
    definition: {
        group: "Xngage Custom" as any,
    },
};

export default widgetModule;
